import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {checkGetAll, checkExcel} from '../../../redux/slices/check.slice'
import {basicLookup, allCompanyLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedHandlers, useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectCheckData = (state) => ({
  check: state.check.check,
  loading: state.check.loading,
})
const selectLookupData = (state) => ({
  companyLookup: state.lookup.companyLookup,
  CheckType: state.lookup.CheckType,
  CheckState: state.lookup.CheckState,
})

function List() {
  const intl = useIntl()
  const {check, loading} = useSelector(selectCheckData)
  const {companyLookup, CheckType, CheckState} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'CHECKS'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(checkGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(basicLookup('CheckType'))
    dispatch(basicLookup('CheckState'))
  }, [dispatch])

  const handlers = useMemoizedHandlers(
    {
      handleSorting: (item) => {
        setParams((prevState) => ({
          ...prevState,
          sort: item && item.sort,
          order: item && item.order,
        }))
      },
      handleSearch: (item) => {
        setParams((prevState) => ({...prevState, name: item}))
      },
      handlePagination: (pagination) => {
        setParams((prevState) => ({...prevState, ...pagination}))
      },
      handleFilterSubmit: (values) => {
        setParams({...values})
      },
    },
    []
  )

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(checkExcel({params, fileName: intl.formatMessage({id: 'BC.CHECKS'})}))
    }
  }, [dispatch, params, intl])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.CHECK'})}
            className='min-w-125px'
          />
        ),
        accessor: 'description',
        Cell: ({...props}) => <CustomCellDouble item={props} double='checkNo' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.CHECKTYPENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'checkTypeName',
        Cell: ({...props}) => <CustomCellDouble item={props} double='bankName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.COMPANYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'fromCompanyName',
        Cell: ({...props}) => <CustomCellDouble item={props} double='toCompanyName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.EDITDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'editDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.DEFERMENTDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'defermentDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.TOTALPRICE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'totalPrice',
        Cell: ({...props}) => <CustomCellDouble item={props} double='checkStateName' price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'CHECK.SEARCH.USEDAMOUNT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'usedAmount',
        Cell: ({...props}) => (
          <CustomCellDouble item={props} double='remainingAmount' price doublePrice />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handlers.handleSorting]
  )

  const buttons = useMemoizedStaticData(() => [], [])

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'CHECK.SEARCH.COMPANYNAME',
            data: companyLookup,
            name: 'companyId',
          },
          {
            title: 'CHECK.SEARCH.CHECKTYPENAME',
            data: CheckType,
            name: 'checkTypeId',
          },
          {
            title: 'CHECK.SEARCH.CHECKSTATEID',
            data: CheckState,
            name: 'checkStateId',
          },
          {
            title: 'CHECK.SEARCH.DEFERMENTSTARTDATE',
            type: 'date',
            name: 'defermentStartDate',
          },
          {
            title: 'CHECK.SEARCH.DEFERMENTENDDATE',
            type: 'date',
            name: 'defermentEndDate',
          },
        ],
      },
    ],
    [companyLookup, CheckType, CheckState, handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.CHECKS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handlers.handleSearch}
          buttons={buttons}
          actionButtons={actionButtons}
          onSubmit={handlers.handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!check && <ListLoading />}
          {check && (
            <Table
              items={check}
              cols={columns}
              handlePagination={handlers.handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
